/**
 * Environment configuration for Nomadian Elite.
 *
 * Reads VITE_* env vars exposed by Vite and validates them at import time.
 * Any module that needs config values should import from here rather than
 * reading import.meta.env directly.
 */

type SolanaNetwork = 'devnet' | 'mainnet-beta'

function requireEnv(key: string): string {
    const value = import.meta.env[key]
    if (!value) {
        throw new Error(
            `Missing required environment variable: ${key}. ` +
            'Copy .env.example to .env and fill in the values.'
        )
    }
    return value
}

function optionalEnv(key: string, fallback: string): string {
    return import.meta.env[key] || fallback
}

function validateNetwork(value: string): SolanaNetwork {
    if (value !== 'devnet' && value !== 'mainnet-beta') {
        throw new Error(
            `Invalid VITE_SOLANA_NETWORK: "${value}". Must be "devnet" or "mainnet-beta".`
        )
    }
    return value
}

function validateUrl(value: string, key: string): string {
    try {
        new URL(value)
    } catch {
        throw new Error(
            `Invalid URL for ${key}: "${value}". Must be a valid URL.`
        )
    }
    return value
}

function validateBase58(value: string, key: string): string {
    if (!/^[1-9A-HJ-NP-Za-km-z]+$/.test(value)) {
        throw new Error(
            `Invalid base58 value for ${key}: "${value}". Must be a valid base58-encoded public key.`
        )
    }
    return value
}

export interface AppConfig {
    supabaseUrl: string
    supabaseAnonKey: string
    privyAppId: string
    solanaRpcUrl: string
    solanaNetwork: SolanaNetwork
    programId: string
    usdcMint: string
    usdtMint: string
    fileUploadWorkerUrl: string
    coinbaseCdpProjectId: string
    /** Platform fee payer public key (pays SOL gas on behalf of users) */
    relayFeePayerPubkey: string
    /** Platform treasury wallet (receives USDC/USDT platform fees) */
    treasuryWallet: string
    /** Production deployer wallet (used for deployment metadata and ownership display) */
    deployerWallet: string
    /** Fixed withdrawal fee in USDC/USDT (e.g., 0.50) */
    withdrawalFee: number
    /** Fixed cancel-investment fee in USDC/USDT (e.g., 0.25) */
    cancelInvestmentFee: number
    isDev: boolean
}

function loadConfig(): AppConfig {
    const supabaseUrl = validateUrl(
        requireEnv('VITE_SUPABASE_URL'),
        'VITE_SUPABASE_URL'
    )

    const supabaseAnonKey = requireEnv('VITE_SUPABASE_ANON_KEY')

    const privyAppId = requireEnv('VITE_PRIVY_APP_ID')

    const isProdBuild = import.meta.env.PROD

    const solanaRpcUrl = validateUrl(
        optionalEnv(
            'VITE_SOLANA_RPC_URL',
            isProdBuild
                ? 'https://solana.publicnode.com'
                : 'https://api.devnet.solana.com'
        ),
        'VITE_SOLANA_RPC_URL'
    )

    const solanaNetwork = validateNetwork(
        optionalEnv('VITE_SOLANA_NETWORK', isProdBuild ? 'mainnet-beta' : 'devnet')
    )

    const programId = validateBase58(
        requireEnv('VITE_PROGRAM_ID'),
        'VITE_PROGRAM_ID'
    )

    const usdcMint = validateBase58(
        requireEnv('VITE_USDC_MINT'),
        'VITE_USDC_MINT'
    )

    const usdtMint = validateBase58(
        requireEnv('VITE_USDT_MINT'),
        'VITE_USDT_MINT'
    )

    return {
        supabaseUrl,
        supabaseAnonKey,
        privyAppId,
        solanaRpcUrl,
        solanaNetwork,
        programId,
        usdcMint,
        usdtMint,
        fileUploadWorkerUrl: optionalEnv('VITE_FILE_UPLOAD_WORKER_URL', ''),
        coinbaseCdpProjectId: optionalEnv('VITE_COINBASE_CDP_PROJECT_ID', ''),
        relayFeePayerPubkey: optionalEnv('VITE_RELAY_FEE_PAYER_PUBKEY', ''),
        treasuryWallet: optionalEnv('VITE_TREASURY_WALLET', ''),
        deployerWallet: optionalEnv('VITE_DEPLOYER_WALLET', ''),
        withdrawalFee: parseFloat(optionalEnv('VITE_WITHDRAWAL_FEE', '0.50')),
        cancelInvestmentFee: parseFloat(optionalEnv('VITE_CANCEL_INVESTMENT_FEE', '0.25')),
        isDev: import.meta.env.DEV
    }
}

export const config = loadConfig()
